import React, { useEffect, useState } from "react";
import { Form, Input, Button, Row, Col, message, Image, Modal } from "antd";
import "./PoProfile.scss";
import { Link, useNavigate } from "react-router-dom";
import { LoadingView } from "../common";
import placementOfficer from "../../api/placementOfficer";
import { getRequest, putRequest } from "../../api/http";
import toastr from "toastr";
import { UserServiceApi } from "../../api";

function PoProfile({ refreshFunction }) {
  const [profileData, setProfileData] = useState({});
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState(null); // New state for profile picture
  const [profilePicURL, setProfilePicURL] = useState(null); // State for profile picture URL
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProfile() {
      try {
        setLoading(true);
        const response = await getRequest(placementOfficer.getProfile());
        setProfileData(response);
        const phoneNumberWithoutPrefix = response?.phone?.replace(
          /^(\+91)/,
          ""
        );

        form.setFieldsValue({
          name: response?.name,
          email: response?.email,
          role: response?.role,
          phone: {
            number: phoneNumberWithoutPrefix,
          },
          address: response?.organizationDTO?.address,
          orgname: response?.organizationDTO?.name,
        });
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [form]);

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1️⃣ Validate type
    if (!file.type.startsWith("image/")) {
      message.error("Please upload only image files (JPG, PNG, JPEG).");
      e.target.value = ""; // reset input
      return;
    }

    // 2️⃣ Optional: validate size (under 5MB)
    const maxSizeMB = 5;
    if (file.size / 1024 / 1024 > maxSizeMB) {
      message.error(`Image size must be less than ${maxSizeMB} MB`);
      e.target.value = ""; // reset input
      return;
    }

    // 3️⃣ Read and convert into Blob + preview URL
    const reader = new FileReader();

    reader.onloadend = () => {
      const blob = new Blob([reader.result], { type: file.type });
      setProfilePic(blob);
      setProfilePicURL(URL.createObjectURL(blob));
    };

    reader.readAsArrayBuffer(file);
  };

  const defaultProfilePic =
    "https://www.shareicon.net/data/512x512/2016/07/26/802043_man_512x512.png";

  const handleSubmit = async (values) => {
    Modal.confirm({
      title: "Are you sure you want to submit?",
      icon: null,
      // content: 'This action cannot be undone.',
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const formData = new FormData();
          const Ph = values?.phone?.number;
          const newPhoneNumber = Ph ? "+91" + Ph.replace(/\s+/g, "") : null;
          const jsonDataBlob = new Blob(
            [
              JSON.stringify({
                phone: newPhoneNumber,
                name: values.name,
              }),
            ],
            { type: "application/json" }
          );
          formData.append("data", jsonDataBlob);

          if (profilePic) {
            formData.append("file", profilePic);
          }
          await putRequest(placementOfficer.getProfile(), formData);
          toastr.success("Your profile has been successfully updated.");
        } catch (error) {
          console.error("Error updating profile:", error);
          toastr.error("Failed to update profile", error.message);
        } finally {
          refreshFunction();
          setLoading(false);
          navigate("/");
        }
      },
      onCancel: () => {
        // Do nothing if user cancels
      },
    });
  };

  if (loading) return <LoadingView />;

  return (
    <div>
      <div className="header-events">
        <h3 className="heading">Your Profile!</h3>
        <Link className="btn ant-btn ant-btn-primary" to="/">
          Back
        </Link>
      </div>
      <div className="form-container">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row>
            <Col span={12}>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: "bold" }}>
                        {/* Profile Picture  */}
                      </span>
                    }
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Image
                      src={
                        profilePicURL ||
                        profileData.profileUrl ||
                        defaultProfilePic
                      }
                      alt="Profile"
                      style={{ width: 400, height: 400 }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicChange}
                      style={{ marginTop: 30 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontWeight: "bold" }}>Name</span>}
                    name="name"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontWeight: "bold" }}>Email</span>}
                    name="email"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontWeight: "bold" }}>Role</span>}
                    name="role"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: "bold" }}>
                        Organisation Name
                      </span>
                    }
                    name="orgname"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontWeight: "bold" }}>Address</span>}
                    name="address"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item label="Phone" name="phone">
                    <Input.Group compact>
                      <Input
                        style={{ width: "10%" }}
                        defaultValue="+91"
                        disabled
                      />
                      <Form.Item
                        noStyle
                        name={["phone", "number"]}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!value || /^\d{10}$/.test(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error("Please enter 10 digits")
                              );
                            },
                          },
                        ]}
                      >
                        <Input
                          style={{ width: "90%" }}
                          placeholder="Enter phone number"
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col
                  span={24}
                  style={{ display: "flex", flexDirection: "row-reverse" }}
                >
                  <Button type="primary" htmlType="submit">
                    Update Profile
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
}

export default PoProfile;
